import React from "react";
import PropTypes from "prop-types";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import Modal from "../../components/ui-components/Modal";
import Badge from "../../components/ui-components/Badge";
import StatusPill from "../Budgets/components/StatusPill";
import { accessLevelBadge } from "./RoleCard";
import { useTemplateDiff, useRoleLifecycle } from "./api";
import { reviewCadenceLabel, reviewStatusLabel, fmtDate } from "./roleFormUtils";

// The four governance statuses StatusPill actually has copy for (see
// STATUS_CONFIG in ../Budgets/components/StatusPill) -- a
// role_lifecycle_event's to_status can also carry a non-status event-kind
// label (e.g. "role.template_adopted", Sub-project E3 Task 3's selective
// template adoption), which must never be handed to StatusPill (it would
// silently fall back to rendering "Draft").
const KNOWN_LIFECYCLE_STATUSES = ["draft", "pending_approval", "active", "retired"];

function LifecycleStatusOrLabel({ value }) {
  return KNOWN_LIFECYCLE_STATUSES.includes(value) ? <StatusPill status={value} /> : <span>{value}</span>;
}

// One row of the lifecycle-history timeline: the transition (or
// creation/template-adoption event), the actor, the date, an optional
// reason, and — when the activation control-gate was overridden — the
// overridden finding codes (TransitionRoleStatus's override_findings).
function LifecycleEventRow({ event }) {
  const actorLabel = event.actorEmail || (event.actorUserId ? `User #${event.actorUserId}` : "System");
  const overrideCodes = event.overrideFindings?.critical_findings;

  let transition;
  if (event.toStatus === "role.template_adopted") {
    transition = <span className="text-sm text-text">Template updates adopted</span>;
  } else if (!event.fromStatus) {
    transition = (
      <span className="inline-flex items-center gap-1.5 text-sm text-text">
        Created as <LifecycleStatusOrLabel value={event.toStatus} />
      </span>
    );
  } else {
    transition = (
      <span className="inline-flex items-center gap-1.5 text-sm text-text">
        <LifecycleStatusOrLabel value={event.fromStatus} />
        <span aria-hidden="true" className="text-text-3">
          →
        </span>
        <LifecycleStatusOrLabel value={event.toStatus} />
      </span>
    );
  }

  return (
    <li className="py-2 border-b border-border last:border-b-0">
      <div className="flex items-center justify-between gap-3">
        {transition}
        <span className="text-xs text-text-3 shrink-0">{fmtDate(event.occurredAt)}</span>
      </div>
      <div className="mt-1 text-xs text-text-2">
        {actorLabel}
        {event.reason ? ` · ${event.reason}` : ""}
      </div>
      {Array.isArray(overrideCodes) && overrideCodes.length > 0 && (
        <div className="mt-1 text-xs text-warning">Override: {overrideCodes.join(", ")}</div>
      )}
    </li>
  );
}

// One label/value row in the governance summary grid.
function SummaryRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border last:border-b-0">
      <span className="text-xs font-medium text-text-3 uppercase tracking-[0.06em] shrink-0">{label}</span>
      <span className="text-sm text-text text-right min-w-0">{children}</span>
    </div>
  );
}

// Lightweight role-detail view (Sub-project E2 Task 7, step 4): the
// governance summary for one organisation role — owner, review cadence +
// next review, status, based-on, includes-sensitive, access level,
// capability count. Opened from a click on the role's name in the
// Organisation table.
//
// The lifecycle-history timeline (below the governance summary) renders the
// role's full role_lifecycle_event audit trail via GET
// /api/v1/roles/{id}/lifecycle — only organisation roles have one
// (platform/template roles are governance-immutable, see ErrRoleImmutable),
// so the fetch is gated on role.type and a non-organisation role shows a
// static "no lifecycle history" note instead.
export default function RoleDetailModal({ role, roleNameByCode, ownerName, onClose, onCompareTemplate }) {
  // Template lineage (Sub-project E3, Task 4): only organisation roles
  // cloned from a template carry both `cloned_from` and
  // `based_on_template_version` — a from-scratch role, or one cloned from
  // another organisation role, has neither, so there's nothing to diff and
  // the query stays disabled (no wasted fetch on every detail-modal open).
  const hasLineage = !!role?.cloned_from && role?.based_on_template_version != null;
  const diffQ = useTemplateDiff(role?.id, { enabled: hasLineage });
  const diff = diffQ.data;

  const isOrgRole = role?.type === "organisation";
  const lifecycleQ = useRoleLifecycle(role?.id, { enabled: isOrgRole });
  const lifecycleEvents = lifecycleQ.data || [];

  if (!role) return null;

  const access = accessLevelBadge(role.risk);
  const basedOnLabel = role.cloned_from ? roleNameByCode.get(role.cloned_from) || role.cloned_from : "—";
  const capCount = (role.capabilities || []).length;

  return (
    <Modal
      show={!!role}
      close={onClose}
      title={role.name}
      description={role.purpose || undefined}
      icon={icon({ name: "shield-halved", style: "solid" })}
      cancelButton=""
      confirmButton="Close"
      confirmAction={onClose}
      size="lg"
    >
      <div className="space-y-1">
        <SummaryRow label="Status">
          <StatusPill status={role.status} />
        </SummaryRow>
        <SummaryRow label="Access level">
          <Badge status={access.status} label={access.label} size="sm" />
        </SummaryRow>
        <SummaryRow label="Based on">{basedOnLabel}</SummaryRow>
        <SummaryRow label="Owner">{ownerName || "Unassigned"}</SummaryRow>
        {hasLineage && diff && (
          <div
            className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-b-0"
            data-testid="template-lineage-line"
          >
            <span className="text-xs text-text-2">
              Based on: {basedOnLabel} template v{role.based_on_template_version} · Current difference:{" "}
              {diff.removed.length} removed, {diff.added.length} added
            </span>
            {diff.behind && (
              <button
                type="button"
                onClick={() => onCompareTemplate?.(role)}
                className="inline-flex shrink-0 items-center gap-1 rounded-full border-0 box-border bg-primary-soft px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary-soft-strong cursor-pointer"
              >
                Newer template · +{diff.added.length}/−{diff.removed.length}
              </button>
            )}
          </div>
        )}
        <SummaryRow label="Review cadence">{reviewCadenceLabel(role.review_frequency_days)}</SummaryRow>
        <SummaryRow label="Next review">{reviewStatusLabel(role.next_review_at)}</SummaryRow>
        <SummaryRow label="Includes sensitive data">{role.includes_sensitive ? "Yes" : "No"}</SummaryRow>
        <SummaryRow label="Capabilities">{capCount === 1 ? "1 capability" : `${capCount} capabilities`}</SummaryRow>
      </div>

      <div className="mt-4 pt-3 border-t border-border">
        <div className="text-xs font-medium text-text-3 uppercase tracking-[0.06em] mb-1">Lifecycle history</div>
        {!isOrgRole && (
          <p className="text-xs text-text-2">No lifecycle history for this role type.</p>
        )}
        {isOrgRole && lifecycleQ.isLoading && (
          <p className="text-xs text-text-2">Loading lifecycle history…</p>
        )}
        {isOrgRole && !lifecycleQ.isLoading && lifecycleEvents.length === 0 && (
          <p className="text-xs text-text-2">No lifecycle events recorded yet.</p>
        )}
        {isOrgRole && !lifecycleQ.isLoading && lifecycleEvents.length > 0 && (
          <ul>
            {lifecycleEvents.map((event) => (
              <LifecycleEventRow key={event.id} event={event} />
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

RoleDetailModal.propTypes = {
  role: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    type: PropTypes.string,
    name: PropTypes.string,
    purpose: PropTypes.string,
    status: PropTypes.string,
    risk: PropTypes.string,
    cloned_from: PropTypes.string,
    based_on_template_version: PropTypes.number,
    review_frequency_days: PropTypes.number,
    next_review_at: PropTypes.string,
    includes_sensitive: PropTypes.bool,
    capabilities: PropTypes.arrayOf(PropTypes.string),
  }),
  roleNameByCode: PropTypes.instanceOf(Map).isRequired,
  ownerName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onCompareTemplate: PropTypes.func,
};
